import { useState, useEffect, useCallback } from 'react';
import { publicApi, authApi, subscriptionsApi, saveTokens, clearTokens, isLoggedIn } from './client';

/**
 * Hook para los datos públicos de la landing page.
 * Carga métricas, testimonios y sponsors desde el backend.
 * Fallback a datos estáticos si el backend no está disponible.
 */
export function usePublicData(language = 'es') {
  const [metrics, setMetrics] = useState({
    activeKids: 500,
    countries: 12,
    videosCreated: 2400,
    connectedScouts: 15,
  });
  const [testimonials, setTestimonials] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [elearningCategories, setElearningCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      try {
        const [metricsData, testimonialsData, sponsorsData, categoriesData] = await Promise.allSettled([
          publicApi.getMetrics(),
          publicApi.getTestimonials(language),
          publicApi.getSponsors(),
          publicApi.getElearningCategories(),
        ]);

        if (cancelled) return;

        if (metricsData.status === 'fulfilled' && metricsData.value?.metrics) {
          setMetrics(metricsData.value.metrics);
        }
        if (testimonialsData.status === 'fulfilled' && testimonialsData.value?.testimonials) {
          setTestimonials(testimonialsData.value.testimonials);
        }
        if (sponsorsData.status === 'fulfilled' && sponsorsData.value?.sponsors) {
          // Aplanar: gold + silver + bronze
          const s = sponsorsData.value.sponsors;
          setSponsors([...(s.gold || []), ...(s.silver || []), ...(s.bronze || [])]);
        }
        if (categoriesData.status === 'fulfilled' && categoriesData.value?.categories) {
          setElearningCategories(categoriesData.value.categories);
        }
      } catch (_) {
        // Silencioso — se usan los valores por defecto
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => { cancelled = true; };
  }, [language]);

  return { metrics, testimonials, sponsors, elearningCategories, loading };
}

/**
 * Hook para el estado de autenticación del usuario
 */
export function useAuth() {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const loadUser = useCallback(async () => {
    if (!isLoggedIn()) {
      setAuthLoading(false);
      return;
    }
    try {
      const [meData, subData] = await Promise.allSettled([
        authApi.me(),
        subscriptionsApi.getMySubscription(),
      ]);

      if (meData.status === 'fulfilled') setUser(meData.value?.user ?? null);
      if (subData.status === 'fulfilled') setSubscription(subData.value?.subscription ?? null);
    } catch (_) {
      clearTokens();
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = useCallback(async (email, password) => {
    const data = await authApi.login(email, password);
    saveTokens(data);
    setUser(data.user);
    await loadUser(); // recargar suscripción también
    return data;
  }, [loadUser]);

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
    setSubscription(null);
  }, []);

  return { user, subscription, authLoading, login, logout, reloadUser: loadUser };
}

/**
 * Hook para los planes de suscripción
 */
export function usePlans() {
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    subscriptionsApi.getPlans()
      .then(data => setPlans(data?.plans ?? []))
      .catch(() => {})
      .finally(() => setPlansLoading(false));
  }, []);

  return { plans, plansLoading };
}
